import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components/LoadingSpinner';
import { emptyState } from '../shared/components/Narrative';
import { infoIcon } from '../shared/components/InfoIcon';
import { icon } from '../shared/components/icons';
import { paginate, pager } from '../shared/lib/tableControls';

const CATEGORY_LABELS: Record<number, string> = {
  1: 'Cleaning', 2: 'Maintenance', 3: 'Check-in', 4: 'Check-out', 5: 'Back Office', 6: 'Other'
};

interface TaskKPIs  { total: number; pending: number; in_progress: number; completed: number; total_cost: number; }
interface TaskRow   { hostaway_task_id: number; listing_name: string; title: string; status: string; category: number|null; can_start_from: string|null; should_end_by: string|null; cost: number|null; cost_currency: string; }
interface ExpenseKPIs { total: number; total_amount: number; this_month: number; avg_per_listing: number; }
interface ExpenseRow  { hostaway_expense_id: number; listing_name: string; expense_date: string|null; concept: string|null; amount: number|null; currency: string; type: string|null; }

const STATUS_META: Record<string, { label: string; pill: string }> = {
  pending:    { label: 'Pending',     pill: 'pill-amber' },
  confirmed:  { label: 'Confirmed',   pill: 'pill-blue' },
  inProgress: { label: 'In Progress', pill: 'pill-purple' },
  completed:  { label: 'Completed',   pill: 'pill-green' },
  cancelled:  { label: 'Cancelled',   pill: 'pill-gray' },
};

function statusChip(s: string): string {
  const m = STATUS_META[s] || { label: s, pill: 'pill-gray' };
  return `<span class="pill ${m.pill}">${m.label}</span>`;
}
function catChip(c: number|null): string {
  if (!c) return '<span style="color:#9ca3af; font-size:12px;">—</span>';
  const l = CATEGORY_LABELS[c] || String(c);
  return `<span style="display:inline-flex; align-items:center; padding:2px 8px; border-radius:6px; font-size:11px; font-weight:500; background:#f1f5f9; color:#475569;">${l}</span>`;
}
function fmtDate(d: string|null, short = false): string {
  if (!d) return '—';
  try {
    const opts: Intl.DateTimeFormatOptions = short
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: '2-digit' };
    return new Date(d + 'T12:00:00').toLocaleDateString('en-US', opts);
  } catch { return d; }
}
// Guard a money value → "$1,234" or "—" when missing / non-finite.
function fmtMoney(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
// Guard a count value → "1,234" or "—".
function fmtCount(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString();
}

export class OperationsPage {
  private container: HTMLElement;
  private activeTab: 'tasks' | 'expenses' = 'tasks';
  private taskStatus   = 'all';
  private taskCategory = 'all';
  private taskSearch   = '';
  private expenseSearch = '';
  private taskPage   = 1;
  private expensePage = 1;
  private readonly perPage = 25;
  private tasks:    TaskRow[]   = [];
  private taskKPIs: TaskKPIs    = { total: 0, pending: 0, in_progress: 0, completed: 0, total_cost: 0 };
  private expenses: ExpenseRow[] = [];
  private expenseKPIs: ExpenseKPIs = { total: 0, total_amount: 0, this_month: 0, avg_per_listing: 0 };
  private syncing = false;

  constructor(container: HTMLElement) { this.container = container; }

  async render(): Promise<void> {
    this.container.innerHTML = this.shell();
    const content = document.getElementById('ops-content');
    if (content) content.innerHTML = `<div style="padding:48px; text-align:center;">${LoadingSpinner.render()}</div>`;
    await this.fetchAll();
    this.renderContent();
    this.bindAll();
  }

  // ── Shell (persistent across tab switches) ────────────────────────────────

  private shell(): string {
    return `
    <div class="page-content">

      <div class="dash-head">
        <h1>Tasks & Expenses</h1>
        <p>Operational work and spend across the portfolio — cleaning and maintenance tasks plus the expenses that hit each property.</p>
      </div>

      <style>
        .ops-filters { padding:14px 18px; display:flex; gap:12px; align-items:center; flex-wrap:wrap; border-bottom:1px solid var(--border); }
        .ops-search { padding:8px 13px; border:1px solid var(--border); border-radius:var(--radius-sm); font-size:var(--fs-body); background:var(--card); color:var(--text); transition:border-color 0.15s ease, box-shadow 0.15s ease; }
        .ops-search::placeholder { color:var(--text-muted); }
        .ops-search:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
        .ops-trunc { max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        @media (max-width:640px) {
          .ops-search { width:100% !important; flex:1 1 100%; }
          .ops-trunc { max-width:140px; }
        }
      </style>

      <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; flex-wrap:wrap; gap:12px;">
        <div class="seg-tabs">
          <button class="seg-tab ${this.activeTab === 'tasks' ? 'active' : ''}" data-tab="tasks">Tasks</button>
          <button class="seg-tab ${this.activeTab === 'expenses' ? 'active' : ''}" data-tab="expenses">Expenses</button>
        </div>
        <button class="btn btn-secondary btn-sm" id="ops-sync-btn" style="display:flex; align-items:center; gap:6px;">
          <span id="ops-sync-icon">↻</span> Sync from Hostaway
        </button>
      </div>

      <div id="ops-content"></div>

    </div>`;
  }

  // ── Data ──────────────────────────────────────────────────────────────────

  private async fetchAll(): Promise<void> {
    try {
      const [tRes, eRes] = await Promise.all([
        fetch('/api/operations/tasks'),
        fetch('/api/operations/expenses'),
      ]);
      if (tRes.ok) { const d = await tRes.json(); this.taskKPIs = d.kpis; this.tasks = d.rows || []; }
      if (eRes.ok) { const d = await eRes.json(); this.expenseKPIs = d.kpis; this.expenses = d.rows || []; }
    } catch (e) { console.error('Operations fetch error:', e); }
  }

  private async syncData(): Promise<void> {
    if (this.syncing) return;
    this.syncing = true;
    const btn  = document.getElementById('ops-sync-btn') as HTMLButtonElement;
    const icon = document.getElementById('ops-sync-icon');
    if (btn)  btn.disabled = true;
    if (icon) icon.textContent = '⟳';
    if (btn)  btn.style.opacity = '0.6';

    try {
      await Promise.all([
        fetch('/api/sync/tasks'),
        fetch('/api/sync/expenses'),
      ]);
      await this.fetchAll();
      this.taskPage = 1;
      this.expensePage = 1;
      this.renderContent();
      this.bindFilterEvents();
    } finally {
      this.syncing = false;
      if (btn)  { btn.disabled = false; btn.style.opacity = '1'; }
      if (icon) icon.textContent = '↻';
    }
  }

  // ── Render helpers ────────────────────────────────────────────────────────

  private renderContent(): void {
    const el = document.getElementById('ops-content');
    if (el) el.innerHTML = this.activeTab === 'tasks' ? this.renderTasks() : this.renderExpenses();
  }

  private filteredTasks(): TaskRow[] {
    const q = this.taskSearch.toLowerCase();
    return this.tasks.filter(t => {
      if (this.taskStatus !== 'all' && t.status !== this.taskStatus) return false;
      if (this.taskCategory !== 'all' && String(t.category) !== this.taskCategory) return false;
      if (q && !t.title?.toLowerCase().includes(q) && !t.listing_name?.toLowerCase().includes(q)) return false;
      return true;
    });
  }

  private filteredExpenses(): ExpenseRow[] {
    const q = this.expenseSearch.toLowerCase();
    return this.expenses.filter(e => {
      if (!q) return true;
      return e.listing_name?.toLowerCase().includes(q) || e.concept?.toLowerCase().includes(q) || e.type?.toLowerCase().includes(q);
    });
  }

  // ── Tasks tab ─────────────────────────────────────────────────────────────

  private renderTasks(): string {
    const filtered = this.filteredTasks();
    const pg = paginate(filtered, this.taskPage, this.perPage);
    this.taskPage = pg.page; // adopt the clamped page
    const total = pg.total;
    const page  = pg.items;

    return `
    <div class="kpi-grid">
      ${this.kpi('Total Tasks',  'wrench',   fmtCount(this.taskKPIs.total),       'all tasks tracked')}
      ${this.kpi('Pending',      'flag',     fmtCount(this.taskKPIs.pending),      'awaiting action')}
      ${this.kpi('In Progress',  'pulse',    fmtCount(this.taskKPIs.in_progress),  'currently underway')}
      ${this.kpi('Completed',    'target',   fmtCount(this.taskKPIs.completed),    'finished', 'tone-good')}
      ${this.kpi('Total Cost',   'wallet',   fmtMoney(this.taskKPIs.total_cost),   'operational spend ' + infoIcon('task_cost'))}
    </div>

    <div class="table-container">
      <div class="ops-filters">
        <input type="text" id="task-search" value="${Formatters.escapeHtml(this.taskSearch)}"
          placeholder="Search tasks or listings…" class="ops-search" style="width:200px;">
        <div style="display:flex; gap:6px; flex-wrap:wrap;">
          ${['all','pending','inProgress','completed','cancelled'].map(s => {
            const lbl = s === 'all' ? 'All' : s === 'inProgress' ? 'In Progress' : s.charAt(0).toUpperCase()+s.slice(1);
            return `<button class="chip ${this.taskStatus === s ? 'active' : ''}" data-status="${s}">${lbl}</button>`;
          }).join('')}
        </div>
        <div style="display:flex; gap:6px; flex-wrap:wrap;">
          ${[{v:'all',l:'All'},{v:'1',l:'Cleaning'},{v:'2',l:'Maintenance'},{v:'3',l:'Check-in'},{v:'4',l:'Check-out'},{v:'6',l:'Other'}].map(c =>
            `<button class="chip ${this.taskCategory === c.v ? 'active' : ''}" data-category="${c.v}">${c.l}</button>`
          ).join('')}
        </div>
        <span style="margin-left:auto; font-size:12px; color:var(--text-muted);">${total.toLocaleString()} tasks</span>
      </div>

      <div class="table-scroll">
        <table style="min-width:700px;">
          <thead>
            <tr>
              <th>Listing</th>
              <th>Task</th>
              <th>Category</th>
              <th>Status</th>
              <th>Due</th>
              <th style="text-align:right;">Cost</th>
            </tr>
          </thead>
          <tbody>
            ${page.length ? page.map(t => `
              <tr>
                <td class="ops-trunc" style="max-width:180px; font-size:12px; color:var(--text-muted);" title="${Formatters.escapeHtml(t.listing_name)}">${Formatters.escapeHtml(t.listing_name || '—')}</td>
                <td class="ops-trunc" style="max-width:240px; font-size:13px; font-weight:500;" title="${Formatters.escapeHtml(t.title||'')}">${Formatters.escapeHtml(t.title||'—')}</td>
                <td>${catChip(t.category)}</td>
                <td>${statusChip(t.status)}</td>
                <td style="font-size:12px; white-space:nowrap; color:${this.dueColor(t.should_end_by)};">${fmtDate(t.should_end_by, true)}</td>
                <td style="text-align:right; font-size:13px; font-weight:500;">${fmtMoney(t.cost)}</td>
              </tr>`).join('')
            : `<tr><td colspan="6" style="padding:0;">${emptyState('No tasks match', 'Try clearing a filter or search term, or sync from Hostaway to pull the latest tasks.', '🧹')}</td></tr>`}
          </tbody>
        </table>
      </div>

      ${this.pagination(total, this.taskPage, 'task')}
    </div>`;
  }

  // ── Expenses tab ──────────────────────────────────────────────────────────

  private renderExpenses(): string {
    const filtered = this.filteredExpenses();
    const pg = paginate(filtered, this.expensePage, this.perPage);
    this.expensePage = pg.page; // adopt the clamped page
    const total = pg.total;
    const page  = pg.items;

    return `
    <div class="kpi-grid">
      ${this.kpi('Total Expenses', 'receipt',  fmtCount(this.expenseKPIs.total),             'expense line items')}
      ${this.kpi('Total Amount',   'wallet',   fmtMoney(this.expenseKPIs.total_amount),      'all-time spend ' + infoIcon('operating_expense'))}
      ${this.kpi('This Month',     'calendar', fmtMoney(this.expenseKPIs.this_month),        'spend this month ' + infoIcon('operating_expense'))}
      ${this.kpi('Avg / Listing',  'home',     fmtMoney(this.expenseKPIs.avg_per_listing),   'per property ' + infoIcon('operating_expense'))}
    </div>

    <div class="table-container">
      <div class="ops-filters">
        <input type="text" id="expense-search" value="${Formatters.escapeHtml(this.expenseSearch)}"
          placeholder="Search listing, concept or type…" class="ops-search" style="width:260px;">
        <span style="margin-left:auto; font-size:12px; color:var(--text-muted);">${total.toLocaleString()} expenses</span>
      </div>

      <div class="table-scroll">
        <table style="min-width:640px;">
          <thead>
            <tr>
              <th>Date</th>
              <th>Listing</th>
              <th>Concept</th>
              <th>Type</th>
              <th style="text-align:right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${page.length ? page.map(e => `
              <tr>
                <td style="font-size:12px; white-space:nowrap; color:var(--text-muted);">${fmtDate(e.expense_date)}</td>
                <td class="ops-trunc" style="max-width:180px; font-size:12px; color:var(--text-muted);" title="${Formatters.escapeHtml(e.listing_name)}">${Formatters.escapeHtml(e.listing_name || '—')}</td>
                <td class="ops-trunc" style="max-width:220px; font-size:13px; font-weight:500;" title="${Formatters.escapeHtml(e.concept||'')}">${Formatters.escapeHtml(e.concept||'—')}</td>
                <td style="font-size:11px; color:#64748b;">${e.type ? Formatters.escapeHtml(e.type) : '—'}</td>
                <td style="text-align:right; font-size:13px; font-weight:600;">${fmtMoney(e.amount, 2)}</td>
              </tr>`).join('')
            : `<tr><td colspan="5" style="padding:0;">${emptyState('No expenses found', 'Try clearing the search term, or sync from Hostaway to pull the latest expenses.', '💸')}</td></tr>`}
          </tbody>
        </table>
      </div>

      ${this.pagination(total, this.expensePage, 'expense')}
    </div>`;
  }

  // ── Small helpers ─────────────────────────────────────────────────────────

  private kpi(label: string, icoName: string, value: string, cap = '', tone = ''): string {
    return `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(icoName, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
  }

  // Footer pager via the shared helper. idPrefix 'task' / 'expense' yields the
  // `${key}-prev` / `${key}-next` button ids the existing handlers already bind.
  private pagination(total: number, page: number, key: 'task' | 'expense'): string {
    const pages = Math.ceil(total / this.perPage) || 1;
    if (pages <= 1) return '';
    return `<div style="padding:0 16px; border-top:1px solid var(--border);">${
      pager({ idPrefix: key, page, pages, total, pageSize: this.perPage })
    }</div>`;
  }

  private dueColor(date: string|null): string {
    if (!date) return 'var(--text-muted)';
    const d = new Date(date + 'T12:00:00');
    const today = new Date(); today.setHours(0,0,0,0);
    if (d < today) return 'var(--danger)';
    const diff = (d.getTime() - today.getTime()) / 86400000;
    if (diff <= 2) return 'var(--warning)';
    return 'var(--text)';
  }

  // ── Events ────────────────────────────────────────────────────────────────

  private bindAll(): void {
    document.getElementById('ops-sync-btn')?.addEventListener('click', () => this.syncData());

    this.container.querySelectorAll<HTMLElement>('[data-tab]').forEach(btn => {
      btn.addEventListener('click', () => {
        const tab = btn.dataset.tab as 'tasks' | 'expenses';
        if (tab === this.activeTab) return;
        this.activeTab = tab;
        this.container.querySelectorAll<HTMLElement>('[data-tab]').forEach(b =>
          b.classList.toggle('active', b.dataset.tab === tab)
        );
        this.renderContent();
        this.bindFilterEvents();
      });
    });

    this.bindFilterEvents();
  }

  // Re-render swaps out the search <input>, so move focus + caret back to the
  // freshly-rendered one (EventPricingPage pattern) to keep typing seamless.
  private refocusSearch(id: string): void {
    const next = document.getElementById(id) as HTMLInputElement | null;
    if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
  }

  private bindFilterEvents(): void {
    this.container.querySelectorAll<HTMLElement>('[data-status]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.taskStatus = btn.dataset.status || 'all';
        this.taskPage = 1;
        this.renderContent();
        this.bindFilterEvents();
      });
    });

    this.container.querySelectorAll<HTMLElement>('[data-category]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.taskCategory = btn.dataset.category || 'all';
        this.taskPage = 1;
        this.renderContent();
        this.bindFilterEvents();
      });
    });

    const tSearch = document.getElementById('task-search') as HTMLInputElement;
    tSearch?.addEventListener('input', () => {
      this.taskSearch = tSearch.value;
      this.taskPage = 1;
      this.renderContent();
      this.bindFilterEvents();
      this.refocusSearch('task-search');
    });

    const eSearch = document.getElementById('expense-search') as HTMLInputElement;
    eSearch?.addEventListener('input', () => {
      this.expenseSearch = eSearch.value;
      this.expensePage = 1;
      this.renderContent();
      this.bindFilterEvents();
      this.refocusSearch('expense-search');
    });

    document.getElementById('task-prev')?.addEventListener('click', () => { this.taskPage--; this.renderContent(); this.bindFilterEvents(); });
    document.getElementById('task-next')?.addEventListener('click', () => { this.taskPage++; this.renderContent(); this.bindFilterEvents(); });
    document.getElementById('expense-prev')?.addEventListener('click', () => { this.expensePage--; this.renderContent(); this.bindFilterEvents(); });
    document.getElementById('expense-next')?.addEventListener('click', () => { this.expensePage++; this.renderContent(); this.bindFilterEvents(); });
  }
}
